import React from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Text } from 'components/Typography';
import { lineHeight, theme, toRem } from 'design';
import { ItemContainer, StyledIconButton, Title, TitleRow } from 'screens/SiteSettings/styles';
import { TipCalculationsDescription } from 'screens/SiteSettings/TipCalculator/shared/styles';
import * as Actions from 'store/Site/actions';
import { ISite, ISiteWithholdings } from 'types';
import { centsToDollarsDisplay } from 'utils';
import EditWithholdingsModal from './EditWithholdingsModal';

const SettingsContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  padding: ${toRem(theme.space.lg)};
  border-radius: ${toRem(8)};
  background-color: ${theme.color.neutral[50]};
  gap: ${toRem(theme.space.xs)};
`;

const SiteSettingsList = styled.ul`
  margin-left: ${toRem(theme.space.lg)};
`;

const SiteSettingsItem = styled.li`
  font-size: ${toRem(theme.font.size.xs)};
  line-height: ${lineHeight(theme.font.size.xs)};
  color: ${theme.color.neutral[500]};

  span {
    font-size: ${toRem(theme.font.size.xs)};
    line-height: ${lineHeight(theme.font.size.xs)};
    font-weight: ${theme.font.weight.semiBold};
  }
`;

interface IDefaultSiteWithholdingsProps {
  currentSite: ISite;
  withholdings: ISiteWithholdings;
  isLoading: boolean;
  readOnly: boolean;
}

const DefaultSiteWithholdings = ({ currentSite, withholdings, isLoading, readOnly }: IDefaultSiteWithholdingsProps) => {
  const dispatch = useDispatch();

  const onEditSave = (updatedWithholdings: ISiteWithholdings) => {
    dispatch(Actions.updateSiteWithholdingsRequest(currentSite.id, updatedWithholdings, { onSuccess: hideEditModal }));
  };

  const [openEditModal, hideEditModal] = useModal(() => <EditWithholdingsModal hideModal={hideEditModal} withholdings={withholdings} isProcessing={isLoading} onSave={onEditSave} />, [currentSite, isLoading]);

  return (
    <>
      <TipCalculationsDescription size="xsmall">Configure the withholding settings for the site.</TipCalculationsDescription>
      <ItemContainer data-cy="default-withholdings-container">
        <TitleRow>
          <Title fontWeight="semibold">Site Withholding Settings</Title>
          {!readOnly && <StyledIconButton label="Edit" onClick={openEditModal} appearance="white" size="small" icon="Edit" disabled={isLoading} data-cy="edit-button" />}
        </TitleRow>
        <SettingsContainer>
          <Text size="xxsmall" fontWeight="medium">
            Settings
          </Text>
          <SiteSettingsList>
            <SiteSettingsItem data-cy="default-withholdings-minimum">
              <span>{centsToDollarsDisplay(withholdings.minimum ?? 0)}</span> Withholding Minimum
            </SiteSettingsItem>
            <SiteSettingsItem data-cy="default-withholdings-rate">
              <span>{`${withholdings.rate}%`}</span> Withholding Rate
            </SiteSettingsItem>
            <SiteSettingsItem data-cy="default-withholdings-cap">
              <span>{!withholdings.cap ? 'No' : centsToDollarsDisplay(withholdings.cap)}</span> Withholding Cap
            </SiteSettingsItem>
          </SiteSettingsList>
        </SettingsContainer>
      </ItemContainer>
    </>
  );
};

export default DefaultSiteWithholdings;
